import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import ChapterList from "./components/ChapterList";
import RelatedComics from "./components/RelatedComics";
import { comics } from "@/mocks/comics";
import { useFavorites } from "@/hooks/useFavorites";
import { formatCount, formatDate, statusLabel } from "@/lib/format";

export default function Detail() {
  const { id } = useParams();
  const comic = comics.find((item) => item.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();

  const favorited = comic ? isFavorite(comic.id) : false;

  useEffect(() => {
    if (!comic) return;
    document.title = `${comic.title} - JMcomic2高清漫画详情`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        `${comic.title} 是 ${comic.author} 创作的${comic.genres.join("、")}题材高清漫画，${statusLabel(comic.status)}，共 ${comic.chapters.length} 话。${comic.description.slice(0, 70)}……`,
      );
    }
  }, [comic]);

  if (!comic) {
    return (
      <div className="w-full px-4 md:px-6 py-24 flex flex-col items-center text-center">
        <span className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center text-foreground-500 mb-4">
          <i className="ri-file-unknow-line text-3xl" />
        </span>
        <h1 className="font-heading font-bold text-xl text-foreground-950">未找到该漫画</h1>
        <p className="mt-2 text-sm text-foreground-500">它可能已被移除，或链接有误</p>
        <Link
          to="/category"
          className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-arrow-left-line" />
          返回分类页
        </Link>
      </div>
    );
  }

  const latest = comic.chapters[comic.chapters.length - 1];
  const firstChapter = comic.chapters[0];

  return (
    <div className="w-full">
      <div className="relative w-full">
        <div className="absolute inset-0 h-[280px] overflow-hidden opacity-40">
          <img
            src={comic.cover}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-50/70 via-background-50/90 to-background-50" />
        </div>

        <div className="relative px-4 md:px-6 py-8">
          <nav className="flex items-center gap-1.5 text-xs text-foreground-500 mb-6 flex-wrap">
            <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/category" className="hover:text-primary-500 transition-colors">分类</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-950 truncate max-w-[200px]">{comic.title}</span>
          </nav>

          <div className="flex flex-col sm:flex-row gap-5 md:gap-8">
            <div className="w-[200px] sm:w-[240px] shrink-0 mx-auto sm:mx-0">
              <div className="relative w-full aspect-[3/4] rounded-lg overflow-hidden border border-background-200">
                <img
                  src={comic.cover}
                  alt={`${comic.title} 高清漫画封面`}
                  title={`${comic.title} JMcomic2 高清漫画在线阅读`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span
                  className={`px-2.5 py-1 rounded-md text-xs font-medium ${
                    comic.status === "completed"
                      ? "bg-secondary-100 text-secondary-900"
                      : "bg-accent-500 text-background-50"
                  }`}
                >
                  {statusLabel(comic.status)}
                </span>
                {comic.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/category?genre=${encodeURIComponent(genre)}`}
                    className="px-2.5 py-1 rounded-md text-xs bg-background-200 text-foreground-700 hover:text-primary-500 transition-colors"
                  >
                    {genre}
                  </Link>
                ))}
              </div>

              <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
                {comic.title}
              </h1>

              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-700">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-3-line text-primary-400" />
                  作者：{comic.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-star-fill text-primary-400" />
                  {comic.rating.toFixed(1)} 分
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line text-primary-400" />
                  {formatCount(comic.views)} 阅读
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-heart-3-line text-primary-400" />
                  {formatCount(comic.favorites + (favorited ? 1 : 0))} 收藏
                </span>
              </div>

              <p className="mt-3 text-sm text-foreground-500 flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                最近更新：{formatDate(comic.updatedAt)}
                {latest && <span className="ml-2">更新至第 {latest.number} 话</span>}
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-3">
                <Link
                  to={`/read/${comic.id}/${firstChapter.id}`}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-book-open-line" />
                  开始阅读
                </Link>
                <Link
                  to={`/read/${comic.id}/${latest.id}`}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-100 border border-background-300 text-foreground-950 font-medium text-sm hover:border-primary-400 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-flashlight-line" />
                  阅读最新
                </Link>
                <button
                  type="button"
                  onClick={() => comic && toggleFavorite(comic.id)}
                  className={`inline-flex items-center gap-2 px-5 py-3 rounded-md font-medium text-sm border transition-colors whitespace-nowrap cursor-pointer ${
                    favorited
                      ? "bg-accent-500 text-background-50 border-accent-500 hover:bg-accent-600"
                      : "bg-background-100 text-foreground-950 border-background-300 hover:border-accent-500 hover:text-accent-500"
                  }`}
                >
                  <i className={favorited ? "ri-bookmark-3-fill" : "ri-bookmark-3-line"} />
                  {favorited ? "已收藏" : "收藏"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 py-8 space-y-8">
        <section className="rounded-lg bg-background-100 border border-background-200 p-4 md:p-6">
          <h2 className="font-heading font-bold text-lg text-foreground-950 mb-3 flex items-center gap-2">
            <i className="ri-file-text-line text-primary-500" />
            作品简介
          </h2>
          <p className="text-sm text-foreground-700 leading-relaxed">{comic.description}</p>
        </section>

        <ChapterList comicId={comic.id} chapters={comic.chapters} />

        <RelatedComics currentId={comic.id} genres={comic.genres} />
      </div>
    </div>
  );
}